import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../navbar';
import Footer from '../footer';
import { Plane, MapPin, ArrowRight, Loader2, Search, Phone, Mail, User, CheckCircle, Shield, Star, Clock, BadgeCheck } from 'lucide-react';
import service from '../../Service/Service';
import PriceDisplay from '../PriceDisplay';

const COUNTRY_CODES = [
  { code: '+44', label: 'GB +44', flag: 'ðŸ‡¬ðŸ‡§' },
  { code: '+1',  label: 'US +1',  flag: 'ðŸ‡ºðŸ‡¸' },
  { code: '+92', label: 'PK +92', flag: 'ðŸ‡µðŸ‡°' },
  { code: '+91', label: 'IN +91', flag: 'ðŸ‡®ðŸ‡³' },
  { code: '+880',label: 'BD +880',flag: 'ðŸ‡§ðŸ‡©' },
  { code: '+971',label: 'AE +971',flag: 'ðŸ‡¦ðŸ‡ª' },
  { code: '+966',label: 'SA +966',flag: 'ðŸ‡¸ðŸ‡¦' },
  { code: '+33', label: 'FR +33', flag: 'ðŸ‡«ðŸ‡·' },
  { code: '+49', label: 'DE +49', flag: 'ðŸ‡©ðŸ‡ª' },
];

const FlightDealLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState(null);

  // Lead capture form state
  const [fullName, setFullName] = useState('');
  const [countryCode, setCountryCode] = useState('+44');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchDeal = async () => {
      if (!slug) { navigate('/'); return; }
      try {
        const response = await service.getDealBySlug(slug);
        if (response.data && response.data.success && response.data.data) {
          setDeal(response.data.data);
          setLoading(false);
        } else {
          setError('Deal not found.');
          setTimeout(() => navigate('/'), 3000);
        }
      } catch (err) {
        setError(`Error: ${err.response?.data?.error || err.message || 'Deal not found'}`);
        setTimeout(() => navigate('/'), 3000);
      }
    };
    fetchDeal();
  }, [slug, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!fullName.trim()) { setFormError('Please enter your full name.'); return; }
    if (!phoneLocal.trim()) { setFormError('Please enter your phone number.'); return; }

    const fullPhone = `${countryCode}${phoneLocal.replace(/\s/g, '')}`;

    setSubmitting(true);
    try {
      const bookingPayload = {
        full_name: fullName.trim(),
        phone: fullPhone,
        email: email.trim() || null,
        departure: deal.origin_code,
        arrival: deal.destination_code,
        departure_name: `${deal.origin_city} (${deal.origin_code})`,
        arrival_name: `${deal.destination_city} (${deal.destination_code})`,
        date: deal.departure_date || '',
        return_date: deal.return_date || '',
        adults: '1',
        children: '0',
        infants: '0',
        flight_type: deal.return_date ? 'return' : 'one-way',
        flight_class: 'economy',
        package_id: -1,
        status: 'search',
        description: `Deal enquiry: ${deal.title || `${deal.origin_city} to ${deal.destination_city}`}`,
        origin_amount: deal.cached_price || 0,
      };

      await service.addBooking(bookingPayload);

      // Also store in localStorage so if they want to search flights it's pre-filled
      localStorage.setItem('booking', JSON.stringify({
        ...bookingPayload,
        name: fullName.trim(),
      }));

      setSubmitted(true);
    } catch (err) {
      console.error('Booking submission error:', err);
      setFormError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <>
        <Helmet><title>Deal Not Found | SkyRise Travels</title><meta name="robots" content="noindex" /></Helmet>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Deal Not Found</h2>
            <p className="text-gray-600 mb-2">{error}</p>
            <p className="text-sm text-gray-500">Redirecting to homepage...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = deal
    ? `Cheap Flights from ${deal.origin_city} to ${deal.destination_city} | Best Deals & Prices`
    : 'Cheap Flights | Best Deals & Prices';
  const pageDescription = deal
    ? `Find the cheapest flights from ${deal.origin_city} to ${deal.destination_city}. Compare prices from multiple airlines and book with SkyRise Travels.`
    : 'Find the cheapest flights to destinations worldwide. Compare prices and book with confidence.';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`cheap flights, ${deal?.origin_city} to ${deal?.destination_city}, flight deals, airline tickets`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={window.location.href} />
        {deal?.image_path && <meta property="og:image" content={`${service.baseURL}/uploads/${deal.image_path}`} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Navbar bg="blue" />

        {/* â”€â”€ Hero Banner â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="bg-secondary text-white pt-24 pb-16 relative">
          {/* subtle grid overlay */}
          <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize:'30px 30px'}} />

          <div className="relative max-w-5xl mx-auto px-4 text-center">
            {deal ? (
              <>
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-5">
                  <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" />
                  Special Deal - Limited Seats
                </div>

                <h1 className="text-3xl md:text-5xl font-extrabold mb-3 leading-tight">
                  {deal.origin_city} <ArrowRight className="inline w-6 h-6 mx-1" /> {deal.destination_city}
                </h1>
                <p className="text-lg md:text-xl text-white/80 mb-8">
                  Cheap flights from <span className="font-semibold text-white">{deal.origin_city} ({deal.origin_code})</span> to <span className="font-semibold text-white">{deal.destination_city} ({deal.destination_code})</span>
                </p>

                {/* Route pill */}
                <div className="inline-flex items-center gap-4 bg-white/10 border border-white/20 rounded-2xl px-6 py-3 mb-8">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{deal.origin_code}</p>
                    <p className="text-xs text-white/70">{deal.origin_city}</p>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <Plane className="w-5 h-5 text-white/60 rotate-90" />
                    {deal.return_date && <span className="text-[10px] bg-white/20 rounded px-1.5 py-0.5">Return</span>}
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{deal.destination_code}</p>
                    <p className="text-xs text-white/70">{deal.destination_city}</p>
                  </div>
                </div>

                {deal.cached_price && (
                  <div>
                    <p className="text-sm text-white/60 mb-1">Starting from</p>
                    <p className="text-5xl md:text-6xl font-black">
                      <PriceDisplay amount={deal.cached_price} displayCurrency="GBP" />
                    </p>
                    <p className="text-sm text-white/60 mt-1">per person</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex justify-center py-12">
                <Loader2 className="w-10 h-10 animate-spin text-white/60" />
              </div>
            )}
          </div>
        </div>

        {/* â”€â”€ Lead Capture Card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        {deal && (
          <div className="max-w-3xl mx-auto px-4 -mt-8 relative z-10 mb-10">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              {/* Card header */}
              <div className="bg-gradient-to-r from-secondary to-[#03045E] px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-full p-2">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-base">Unlock This Deal</p>
                    <p className="text-white/70 text-xs">Enter your details to see available flights</p>
                  </div>
                </div>
                {deal.cached_price && (
                  <div className="bg-yellow-400 text-yellow-900 font-bold text-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow">
                    <Plane className="w-4 h-4" />
                    from <PriceDisplay amount={deal.cached_price} displayCurrency="GBP" className="inline" />
                  </div>
                )}
              </div>

              {/* Form body */}
              <div className="p-6">
                {submitted ? (
                  <div className="text-center py-6">
                    <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">We've Got Your Request!</h3>
                    <p className="text-gray-600 mb-1">One of our travel experts will call you shortly to confirm availability and pricing.</p>
                    <p className="text-sm text-gray-400 mt-4">Want to search yourself instead?</p>
                    <button
                      onClick={() => navigate('/flight-detail-search?package=0')}
                      className="mt-2 inline-flex items-center gap-2 text-secondary font-semibold text-sm hover:underline"
                    >
                      <Search className="w-4 h-4" /> Search Flights Now
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      {/* Full Name */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                          <User className="w-3.5 h-3.5" /> Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. John Smith"
                          value={fullName}
                          onChange={e => setFullName(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:border-secondary focus:ring-1 focus:ring-secondary outline-none transition"
                        />
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" /> Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-1">
                          <select
                            value={countryCode}
                            onChange={e => setCountryCode(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2 py-2.5 text-xs focus:border-secondary outline-none bg-gray-50 cursor-pointer"
                          >
                            {COUNTRY_CODES.map(c => (
                              <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            placeholder="7700 900000"
                            value={phoneLocal}
                            onChange={e => setPhoneLocal(e.target.value.replace(/[^0-9 ]/g, ''))}
                            className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:border-secondary focus:ring-1 focus:ring-secondary outline-none transition"
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" /> Email <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:border-secondary focus:ring-1 focus:ring-secondary outline-none transition"
                        />
                      </div>
                    </div>

                    {formError && (
                      <p className="text-red-600 text-sm mb-3 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{formError}</p>
                    )}

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-secondary hover:bg-[#03045E] disabled:opacity-60 text-white font-bold px-8 py-3 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        {submitting ? 'Submitting...' : 'Check Availability'}
                      </button>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-green-500" /> No spam</span>
                        <span className="flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-green-500" /> Free</span>
                        <span className="flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-green-500" /> Secure</span>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* â”€â”€ Trust Badges â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="max-w-5xl mx-auto px-4 mb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: <Shield className="w-6 h-6 text-secondary" />, title: 'Secure Booking', desc: 'Your data is safe with us' },
              { icon: <Clock className="w-6 h-6 text-secondary" />, title: '24/7 Support', desc: 'We\'re always available' },
              { icon: <BadgeCheck className="w-6 h-6 text-secondary" />, title: 'Best Price', desc: 'Price match guarantee' },
              { icon: <Star className="w-6 h-6 text-secondary fill-secondary" />, title: '5-Star Rated', desc: 'Trusted by thousands' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
                <div className="flex justify-center mb-2">{item.icon}</div>
                <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* â”€â”€ SEO Content â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="max-w-5xl mx-auto px-4 pb-16">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {deal ? `Flights from ${deal.origin_city} to ${deal.destination_city}` : 'Why Book With Us?'}
            </h2>
            <ul className="space-y-2 text-gray-700 mb-8">
              {[
                'Compare prices from multiple airlines in one place',
                'Best fare guarantee - we search both Duffel and Amadeus',
                'Easy booking with secure payment',
                '24/7 customer support from our travel experts',
                'Flexible one-way and return options',
                'No hidden fees - fully transparent pricing',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-3">How to Book</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              {deal
                ? `Fill in your details above and one of our travel experts will contact you with the best available fares from ${deal.origin_city} to ${deal.destination_city}. We search all major airlines so you don't have to.`
                : 'Fill in your details and one of our experts will find the best available fares for you.'
              }
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/flight-detail-search?package=0')}
                className="inline-flex items-center gap-2 bg-secondary text-white font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-[#03045E] transition-all shadow-sm"
              >
                <Search className="w-4 h-4" /> Search All Flights
              </button>
              <a
                href="tel:02081949506"
                className="inline-flex items-center gap-2 border border-secondary text-secondary font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-secondary/5 transition-all"
              >
                <Phone className="w-4 h-4" /> Call 020 8194 9506
              </a>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};


export default FlightDealLanding;
